import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ApiError } from '@/lib/fetcher';
import {
  accountKey,
  regenerateApiKey,
  type RegenerateApiKeyResponse,
} from '@/lib/queries';

/**
 * Two-step regen dialog: confirm → reveal. Brief 40.
 *
 * Step 1 (confirm): warning + "Regenerate" CTA. The CTA disables
 * during the mutation; on success the dialog transitions to step 2
 * with the raw key in component state.
 *
 * Step 2 (reveal): monospaced full key + copy-to-clipboard +
 * required "I've saved my new key" acknowledgement. The dialog
 * cannot close until the checkbox is ticked. On close, the key is
 * cleared from React state and the Account query is invalidated so
 * the prefix display refreshes.
 *
 * The raw key is in-memory only — never persisted to localStorage,
 * never logged.
 */
export function RegenerateKeyDialog() {
  const [open, setOpen] = React.useState(false);
  const [step, setStep] = React.useState<'confirm' | 'reveal'>('confirm');
  const [regenerated, setRegenerated] =
    React.useState<RegenerateApiKeyResponse | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);

  const qc = useQueryClient();
  const regen = useMutation({
    mutationFn: () => regenerateApiKey(),
    onSuccess: (res) => {
      setRegenerated(res);
      setStep('reveal');
      void qc.invalidateQueries({ queryKey: accountKey });
    },
  });

  const reset = () => {
    setStep('confirm');
    setRegenerated(null);
    setCopied(false);
    setAcknowledged(false);
    regen.reset();
  };

  const handleOpenChange = (next: boolean) => {
    // While in reveal step, block close until the user acknowledges
    // they've saved the key. Brief 40 § B "No 'show again' — the key
    // is gone from frontend state after dismissal."
    if (step === 'reveal' && !acknowledged && !next) return;
    setOpen(next);
    if (!next) reset();
  };

  const copyKey = async () => {
    if (!regenerated?.vfApiKey) return;
    try {
      await navigator.clipboard.writeText(regenerated.vfApiKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Regenerate
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[520px]">
        {step === 'confirm' ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Regenerate API key?
              </DialogTitle>
              <DialogDescription>
                This will mint a new API key and invalidate your current
                one immediately. Any script, agent, or integration using
                the old key will start returning 401 until you update it
                with the new value.
              </DialogDescription>
            </DialogHeader>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>The new key is shown once.</AlertTitle>
              <AlertDescription>
                Relaystation never stores raw API keys; we only keep the
                prefix. If you lose the new value, you'll have to
                regenerate again.
              </AlertDescription>
            </Alert>
            {regen.error ? (
              <Alert variant="destructive">
                <AlertTitle>
                  Couldn't regenerate (
                  {regen.error instanceof ApiError
                    ? regen.error.code
                    : 'unknown'}
                  )
                </AlertTitle>
                <AlertDescription>
                  {regen.error instanceof Error
                    ? regen.error.message
                    : 'Try again.'}
                </AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={regen.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => regen.mutate()}
                disabled={regen.isPending}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {regen.isPending ? 'Regenerating…' : 'Regenerate key'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Your new API key</DialogTitle>
              <DialogDescription>
                Copy it now. The old key is no longer valid. This is the
                only time the full secret is shown.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md border bg-muted p-3 font-mono text-xs break-all">
                {regenerated?.vfApiKey}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyKey}
                className="gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy to clipboard
                  </>
                )}
              </Button>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-input"
                />
                <span>
                  I've saved my new key somewhere safe. I understand
                  Relaystation cannot show it again.
                </span>
              </label>
            </div>
            <DialogFooter>
              <Button
                onClick={() => handleOpenChange(false)}
                disabled={!acknowledged}
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
